import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowDown } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import AIMeasureDemo from "@/components/AIMeasureDemo";
import { FadeIn, Chapter } from "@/components/Motion";
import { FUTURE_TECH, IMAGES } from "@/data/content";

const FLAGSHIP = [
  {
    slug: "cdrive",
    name: "CDRIVE™",
    tagline: "Brushless Performance. Absolute Silence.",
    text: "The proprietary brushless drivetrain behind the quietest stairlift in the world. No brushes, no gear noise, minimal maintenance — 38 dB, every ride.",
    image: IMAGES.cdrive,
    dark: true,
    badges: ["38 dB", "0 brushes", "24V DC", "50k cycle life"],
  },
  {
    slug: "wsafe",
    name: "WSAFE™",
    tagline: "Engineered for Every Season.",
    text: "Outdoor protection technology sealing every board, bearing and connector against rain, UV and corrosion — enabling the world's only outdoor curved stairlift.",
    image: IMAGES.wsafe,
    dark: false,
    badges: ["IPX4 electronics", "UV stabilised", "−15°C to +45°C", "Marine-grade"],
  },
];

export default function Technologies() {
  return (
    <>
      <PageHero
        overline="Technology Ecosystem"
        lines={["The heart of", "the ACTIVE platform"]}
        sub="Proprietary technologies that give distributors arguments competitors cannot copy — and a confidential pipeline that keeps it that way."
      />

      <section className="bg-white pb-24" data-testid="tech-flagships">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {FLAGSHIP.map((t, i) => (
            <FadeIn key={t.slug} delay={0.05}>
              <Link
                to={`/technologies/${t.slug}`}
                data-testid={`tech-card-${t.slug}`}
                className={`group grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden border transition-all duration-300 hover:scale-[1.005] ${
                  t.dark ? "bg-obsidian border-white/10 noise-dark" : "bg-[#F8F9FA] border-slate-200/80 hover:border-signal/40"
                } ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="overflow-hidden spotlight-frame">
                  <img src={t.image} alt={t.name} loading="lazy" className="w-full h-72 md:h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="relative z-[2] p-8 sm:p-12 flex flex-col justify-center">
                  <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${t.dark ? "text-signal" : "text-slate-400"}`}>
                    Proprietary Technology
                  </p>
                  <h2 className={`mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tighter ${t.dark ? "text-white" : "text-slate-950"}`}>
                    {t.name}
                  </h2>
                  <p className={`mt-2 text-base font-medium ${t.dark ? "text-slate-300" : "text-slate-700"}`}>{t.tagline}</p>
                  <p className={`mt-4 text-sm leading-relaxed ${t.dark ? "text-slate-400" : "text-slate-600"}`}>{t.text}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {t.badges.map((b) => (
                      <span key={b} className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1.5 border ${
                        t.dark ? "border-white/15 text-slate-300" : "border-slate-300 text-slate-500"
                      }`}>
                        {b}
                      </span>
                    ))}
                  </div>
                  <span className={`mt-7 inline-flex items-center gap-2 text-sm font-medium ${t.dark ? "text-white group-hover:text-signal" : "text-slate-950 group-hover:text-signal"} transition-colors`}>
                    Explore {t.name} <ArrowUpRight size={15} />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="future-tech">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label="Confidential Pipeline" light />
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05] max-w-2xl">
            What comes next
          </h2>
          <FadeIn delay={0.1}>
            <p className="mt-5 text-base text-slate-400 max-w-xl leading-relaxed">
              Four platforms in development. Distributors on the ACTIVE network get first access, first training and first territory rights.
            </p>
          </FadeIn>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FUTURE_TECH.map((f, i) => (
              <FadeIn key={f.slug} delay={i * 0.07}>
                <div className="group h-full rounded-2xl border border-white/10 bg-surface/60 p-8 hover:border-signal/50 transition-colors duration-300" data-testid={`future-${f.slug}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-amber-400/90 border border-amber-400/30 rounded-full px-2.5 py-1">
                      Coming Soon
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-white">{f.name}</h3>
                  <p className="mt-1.5 text-sm font-medium text-slate-300">{f.tagline}</p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{f.text}</p>
                  {f.slug === "aimeasure" && (
                    <a
                      href="#aimeasure-demo"
                      data-testid="aimeasure-demo-link"
                      className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-signal hover:text-white transition-colors"
                    >
                      Try the interactive preview <ArrowDown size={13} />
                    </a>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <Link
              to="/contact?type=distributor"
              data-testid="future-cta"
              className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-signal transition-colors"
            >
              Join the network for early access <ArrowRight size={15} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section id="aimeasure-demo" className="bg-white py-24 sm:py-32" data-testid="aimeasure-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="03" label="AIMEASURE™ — Interactive Preview" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
                From photo to rail. Drag to see.
              </h2>
              <FadeIn delay={0.15}>
                <p className="mt-6 text-base text-slate-600 leading-relaxed">
                  Take photographs of any staircase. AIMEASURE™ generates the staircase model and the production-ready rail automatically — no laser scanner, no expensive equipment.
                </p>
                <div className="mt-8 space-y-0">
                  {[
                    { n: "01", t: "Photograph", d: "Dealer photographs the staircase with a smartphone." },
                    { n: "02", t: "AI Analysis", d: "Geometry, angles and radii extracted automatically." },
                    { n: "03", t: "Rail Generated", d: "Production-ready rail geometry sent to the factory." },
                  ].map((s) => (
                    <div key={s.n} className="flex gap-5 py-4 border-b border-slate-200/80 first:border-t" data-testid={`aimeasure-step-${s.n}`}>
                      <span className="font-mono text-[11px] text-signal tracking-[0.15em] shrink-0 mt-1">{s.n}</span>
                      <div>
                        <h3 className="font-display text-base font-semibold tracking-tight text-slate-950">{s.t}</h3>
                        <p className="mt-1 text-sm text-slate-600">{s.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <span className="mt-6 inline-block font-mono text-[9px] tracking-[0.2em] uppercase text-amber-600 border border-amber-300 rounded-full px-2.5 py-1">
                  Coming Soon — Concept Preview
                </span>
              </FadeIn>
            </div>
            <div className="lg:col-span-8">
              <FadeIn delay={0.2}>
                <AIMeasureDemo />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
